import { useTranslation } from "react-i18next";

import type { DayStats, ProjectRef } from "@/api/queries";
import { Tooltip } from "@/components/ui/tooltip";
import { formatDuration, formatLongDate, formatWeekday } from "@/lib/format";
import { cn } from "@/lib/utils";

const HEIGHT = 200;

function formatDayNumber(date: string): string {
  return String(Number(date.slice(8, 10)));
}
const HOUR = 3600;

/** A round axis maximum (in hours) and its step. */
function scale(maxSeconds: number): { max: number; step: number } {
  const hours = Math.max(1, maxSeconds / HOUR);
  const step = [1, 2, 4, 5, 10, 20, 25, 50, 100, 200, 250, 500].find((s) => hours / s <= 5) ?? 1000;
  return { max: Math.ceil(hours / step) * step, step };
}

type WeekChartProps = {
  days: DayStats[];
  projects: ProjectRef[];
  today: string;
  lang: string;
};

/** Hours per day, stacked by project; the first project sits at the bottom.
 * Longer periods (reports) show day numbers and hide the values above bars. */
export function WeekChart({ days, projects, today, lang }: WeekChartProps) {
  const { t } = useTranslation();
  const long = days.length > 7;
  const showValues = days.length <= 14;
  const labelEvery = Math.ceil(days.length / 16);
  const byId = new Map(projects.map((p) => [p.id, p]));
  // A stable order of projects across days: by their time over the week.
  const weekly = new Map<string, number>();
  for (const d of days) {
    for (const p of d.projects) weekly.set(p.project_id, (weekly.get(p.project_id) ?? 0) + p.seconds);
  }
  const order = [...weekly.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
  const worked = (d: DayStats) => d.tracked_seconds + d.manual_seconds;
  const { max, step } = scale(Math.max(0, ...days.map(worked)));
  const ticks = Array.from({ length: max / step + 1 }, (_, i) => i * step);
  const px = (seconds: number) => (seconds / (max * HOUR)) * HEIGHT;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
        {order.map((id) => (
          <span key={id} className="flex items-center gap-1.5">
            <span
              className="size-2.5 rounded-[3px]"
              style={{ backgroundColor: byId.get(id)?.color }}
              aria-hidden="true"
            />
            {byId.get(id)?.name ?? "—"}
          </span>
        ))}
      </div>
      <div className="relative pl-10" style={{ height: HEIGHT + 24 }} aria-hidden="true">
        {ticks.map((h) => (
          <div key={h} className="absolute inset-x-0" style={{ bottom: px(h * HOUR) }}>
            <div className={cn("ml-10 border-t", h > 0 && "border-dashed")} />
            <span className="absolute -top-2 left-0 text-[11px] text-muted tabular-nums">
              {t("charts.hours", { count: h })}
            </span>
          </div>
        ))}
        <div className="relative flex h-full items-end gap-1">
          {days.map((d) => {
            const parts = order
              .map((id) => ({ id, seconds: d.projects.find((p) => p.project_id === id)?.seconds ?? 0 }))
              .filter((p) => p.seconds > 0);
            const future = d.date > today;
            return (
              <Tooltip
                key={d.date}
                content={
                  <div className="flex flex-col gap-0.5 py-0.5">
                    <span className="font-medium">
                      {formatLongDate(d.date, lang)} · {formatDuration(worked(d), lang)}
                    </span>
                    {d.manual_seconds > 0 && (
                      <span>{t("charts.manual", { time: formatDuration(d.manual_seconds, lang) })}</span>
                    )}
                    {parts.map((p) => (
                      <span key={p.id} className="flex items-center gap-1.5">
                        <span
                          className="size-2 rounded-[2px]"
                          style={{ backgroundColor: byId.get(p.id)?.color }}
                        />
                        {byId.get(p.id)?.name ?? "—"}: {formatDuration(p.seconds, lang)}
                      </span>
                    ))}
                  </div>
                }
              >
                <button
                  type="button"
                  tabIndex={-1}
                  className="group flex h-full min-w-0 flex-1 cursor-default flex-col items-center justify-end gap-1.5 rounded-md hover:bg-surface-muted/60"
                >
                  {showValues && (
                    <span className="text-xs font-medium tabular-nums">
                      {worked(d) > 0 ? formatDuration(worked(d), lang) : future ? "" : "—"}
                    </span>
                  )}
                  <span className="flex w-full max-w-11 flex-col-reverse gap-0.5">
                    {parts.map((p, i) => (
                      <span
                        key={p.id}
                        className={cn("block w-full", i === parts.length - 1 && "rounded-t")}
                        style={{ height: px(p.seconds), backgroundColor: byId.get(p.id)?.color }}
                      />
                    ))}
                  </span>
                </button>
              </Tooltip>
            );
          })}
        </div>
      </div>
      <div className="flex gap-1 pl-10" aria-hidden="true">
        {days.map((d, i) => (
          <span
            key={d.date}
            className={cn(
              "min-w-0 flex-1 truncate text-center text-xs text-muted",
              d.date === today && "font-semibold text-fg",
            )}
          >
            {!long ? formatWeekday(d.date, lang) : i % labelEvery === 0 ? formatDayNumber(d.date) : ""}
          </span>
        ))}
      </div>
      <table className="sr-only">
        <caption>{t("charts.weekTable")}</caption>
        <thead>
          <tr>
            <th>{t("charts.day")}</th>
            <th>{t("charts.total")}</th>
            {order.map((id) => (
              <th key={id}>{byId.get(id)?.name ?? "—"}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.date}>
              <td>{formatLongDate(d.date, lang)}</td>
              <td>{formatDuration(worked(d), lang)}</td>
              {order.map((id) => (
                <td key={id}>
                  {formatDuration(d.projects.find((p) => p.project_id === id)?.seconds ?? 0, lang)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
